import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  BarChart3,
  MessageCircle,
  HelpCircle,
  Settings,
  Sparkles,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { UdvegaLogo } from './UdvegaLogo';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/teams', label: 'Teams', icon: Users },
  { to: '/surveys', label: 'Surveys', icon: ClipboardList },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/chatbot', label: 'Chatbot', icon: MessageCircle },
  { to: '/faq', label: 'FAQ', icon: HelpCircle },
  { to: '/pricing', label: 'Upgrade', icon: Sparkles },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function CompanyBrandMark({ user }) {
  const name = user?.company_name || user?.company || '';
  const logoUrl = user?.company_logo_url;
  const initial = (name || 'C').charAt(0).toUpperCase();

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${name || 'Company'} logo`}
        className="h-10 w-10 object-contain rounded-lg border border-slate-200 bg-white p-1 shrink-0"
      />
    );
  }

  return (
    <div className="h-10 w-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
      <span className="text-sm font-heading font-bold text-teal-600">{name ? initial : '—'}</span>
    </div>
  );
}

function SidebarContent({ user, onLogout, onNavigate }) {
  const companyLabel = user?.company_name || user?.company;
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-slate-200/70">
        <UdvegaLogo size="sm" />
        <div className="mt-4 flex items-center gap-3">
          <CompanyBrandMark user={user} />
          <div className="min-w-0">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Organization</p>
            <p className="text-sm font-medium text-slate-800 truncate">
              {companyLabel || 'Not set'}
            </p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.filter(({ to }) => {
          if (to !== '/pricing') return true;
          return !!(user?.is_owner || user?.permissions?.manage_billing);
        }).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`sidebar-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/20'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
              }`
            }
          >
            <Icon className="w-[18px] h-[18px]" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-slate-200/70">
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white text-sm font-semibold shrink-0">
            {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{user?.name || 'HR Manager'}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="sidebar-logout"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> Logout
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-40">
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <UdvegaLogo size="sm" />
        <button onClick={() => setOpen(true)} className="p-2" data-testid="mobile-sidebar-toggle">
          <Menu className="w-5 h-5 text-slate-700" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-slate-200">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-3 p-1 z-10">
              <X className="w-5 h-5 text-slate-500" />
            </button>
            <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      {/* Content */}
      <main className="md:ml-64 pt-14 md:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
